/**
 * Option lists for country, currency and time zone.
 *
 * Derived from `Intl` rather than shipped as a data file, for three reasons: the lists are
 * already in every browser, so this adds nothing to the bundle; the names arrive in the
 * user's own language, so the Arabic UI needs no second translation table; and a bundled
 * list is a list that goes stale — currencies are withdrawn and zones are renamed, and
 * nobody remembers to update a JSON file about Kazakhstan.
 *
 * Every function is tolerant of an engine that lacks the newer APIs: it falls back to a
 * short list of the codes this product is actually sold into, so the dropdowns are never
 * empty. An empty dropdown is worse than a text box.
 */

export interface LocaleOption {
  value: string
  label: string
}

/** Where this product is sold, for the fallbacks and for sorting the useful ones first. */
const PRIMARY_COUNTRIES = ['AE', 'SA', 'QA', 'OM', 'BH', 'KW', 'IN', 'PK', 'BD', 'PH', 'GB', 'US']
const PRIMARY_CURRENCIES = ['AED', 'SAR', 'QAR', 'OMR', 'BHD', 'KWD', 'INR', 'PKR', 'BDT', 'USD', 'EUR', 'GBP']
const PRIMARY_TIMEZONES = [
  'Asia/Dubai', 'Asia/Riyadh', 'Asia/Qatar', 'Asia/Muscat', 'Asia/Bahrain', 'Asia/Kuwait',
  'Asia/Kolkata', 'Asia/Karachi', 'Asia/Dhaka', 'Asia/Manila', 'Europe/London', 'UTC',
]

function displayName(code: string, type: 'region' | 'currency', locale: string): string | null {
  try {
    const name = new Intl.DisplayNames([locale], { type, fallback: 'none' }).of(code)
    return name && name !== code ? name : null
  } catch {
    return null
  }
}

function byLabel(a: LocaleOption, b: LocaleOption) {
  return a.label.localeCompare(b.label)
}

/**
 * Every ISO 3166-1 alpha-2 country, named in {@code locale}.
 *
 * <p>`Intl` has no list of regions to enumerate — only a way to name one — so the 676
 * two-letter combinations are generated and the ones `Intl.DisplayNames` declines to name
 * are dropped. With `fallback: 'none'` it returns undefined for a code that is not a real
 * region, which makes the filter exact rather than a guess, and leaves about 250.
 */
export function countryOptions(locale = 'en'): LocaleOption[] {
  const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'
  const options: LocaleOption[] = []

  for (const first of letters) {
    for (const second of letters) {
      const code = first + second
      const label = displayName(code, 'region', locale)
      if (label) options.push({ value: code, label: `${label} (${code})` })
    }
  }

  if (options.length === 0) {
    return PRIMARY_COUNTRIES.map((code) => ({ value: code, label: code }))
  }
  return options.sort(byLabel)
}

/** Every currency the engine knows, named in {@code locale}. */
export function currencyOptions(locale = 'en'): LocaleOption[] {
  let codes: string[]
  try {
    codes = Intl.supportedValuesOf('currency')
  } catch {
    codes = PRIMARY_CURRENCIES
  }

  return codes
    .map((code) => {
      const label = displayName(code, 'currency', locale)
      return { value: code, label: label ? `${code} — ${label}` : code }
    })
    .sort(byLabel)
}

/** Every IANA time zone the engine knows. Not translated: a zone id is an identifier. */
export function timezoneOptions(): LocaleOption[] {
  let zones: string[]
  try {
    zones = Intl.supportedValuesOf('timeZone')
  } catch {
    zones = PRIMARY_TIMEZONES
  }
  if (zones.length === 0) zones = PRIMARY_TIMEZONES

  return zones
    .map((zone) => ({ value: zone, label: zone.replace(/_/g, ' ') }))
    .sort(byLabel)
}

/**
 * The symbol a currency is written with, for prefilling the company's own setting.
 *
 * <p>Formats zero and strips the digits and separators, which is how the symbol is obtained
 * without a table of them. Falls back to the code — "AED 1,200" is correct and plain, and a
 * wrong symbol is worse than no symbol.
 */
export function currencySymbol(code: string, locale = 'en'): string {
  if (!code) return ''
  try {
    const formatted = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: code,
      currencyDisplay: 'narrowSymbol',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(0)
    // Escapes, not the characters themselves: a literal no-break space in source is
    // invisible to the next reader and the linter rejects it.
    const symbol = formatted.replace(/[\d\s.,\u00a0\u202f]/g, '').trim()
    return symbol || code
  } catch {
    return code
  }
}
